import {
  Box,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import type { ThresholdBundleFormController } from './threshold-bundle-form.hook';
import { ThresholdPeriodRangePicker } from './ThresholdPeriodRangePicker';

// -- type definitions --

export interface ThresholdAssignmentStepProps {
  controller: ThresholdBundleFormController;
}

// -- functions --

const getGroupLabel = (groupKey: string): string => groupKey;

const toPlural = (count: number, singular: string, plural: string): string =>
  count > 1 ? plural : singular;

// -- main component --

export function ThresholdAssignmentStep({
  controller,
}: ThresholdAssignmentStepProps) {
  const {
    formValues,
    availableSources,
    selectedSourceGroupKeys,
    setSelectedSourceGroupKeys,
  } = controller;
  const errors = controller.validationErrors;

  const groupOptions = Object.keys(availableSources);
  const selectedGroups = selectedSourceGroupKeys.map((groupKey) => ({
    key: groupKey,
    label: getGroupLabel(groupKey),
    sources: availableSources[groupKey] ?? [],
  }));
  const selectedSources = selectedGroups.flatMap((group) => group.sources);
  const allSelectedGroupsAreSingleSource =
    selectedGroups.length > 0 &&
    selectedGroups.every((group) => group.sources.length <= 1);
  const allOptionsAreSingleSource = groupOptions.every(
    (groupKey) => (availableSources[groupKey] ?? []).length <= 1
  );

  return (
    <Stack spacing={3}>
      <ThresholdPeriodRangePicker
        label="Période d'assignation"
        value={[
          formValues.assignment.active_from,
          formValues.assignment.active_to,
        ]}
        onChange={([activeFrom, activeTo]) => {
          controller.setAssignmentField('active_from', activeFrom);
          controller.setAssignmentField('active_to', activeTo);
        }}
        helperText={errors.assignment_period ?? 'Optionnel'}
        error={Boolean(errors.assignment_period)}
      />

      <Divider />

      <Stack spacing={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>
          Assignation
        </Typography>

        {groupOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Aucune source disponible n'a été fournie.
          </Typography>
        ) : (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={groupOptions}
            value={selectedSourceGroupKeys}
            onChange={(_, value) => setSelectedSourceGroupKeys(value)}
            getOptionLabel={(key) => getGroupLabel(key)}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label={
                  allOptionsAreSingleSource
                    ? "Sources d'assignation"
                    : "Groupes d'assignation"
                }
                error={Boolean(errors.assignment_source_group)}
                helperText={
                  errors.assignment_source_group ?? allOptionsAreSingleSource
                    ? 'Sélectionnez une ou plusieurs sources à assigner au seuil'
                    : 'Sélectionnez un ou plusieurs groupes à assigner au seuil'
                }
              />
            )}
          />
        )}

        {selectedSources.length > 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {allSelectedGroupsAreSingleSource
                ? `${selectedSources.length} ${toPlural(
                    selectedSources.length,
                    'source sélectionnée :',
                    'sources sélectionnées :'
                  )}`
                : `${selectedGroups.length} ${toPlural(
                    selectedGroups.length,
                    'groupe sélectionné :',
                    'groupes sélectionnés :'
                  )}`}
            </Typography>
            {allSelectedGroupsAreSingleSource ? (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {selectedSources.map((source) => (
                  <Chip
                    key={`${source.measure_point_id}-${source.measure_type_id}`}
                    label={source.label}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            ) : (
              <Stack spacing={1.25}>
                {selectedGroups.map((group) => (
                  <Box key={group.key}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.5 }}
                    >
                      {group.label}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {group.sources.map((source) => (
                        <Chip
                          key={`${group.key}-${source.measure_point_id}-${source.measure_type_id}`}
                          label={source.label}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
